
import { computed, defineComponent, onMounted, Ref, ref } from 'vue'
import LoadingButton from '@/views/components/LoadingButton.vue'
import ContentLayout from '@/views/layout/ContentLayout.vue'
import { keieiJohoRepositories } from '@/repositories/keiei-joho/keieiJohoRepositories'
import { callApiWrapper } from '@/views/callApiWrapper'
import ResponseResultAlert from '@/views/components/ResponseResultAlert.vue'
import { ShokushuUiItem } from '@/views/keiei-joho/data'
import { UiItemsLogic } from '@/views/fuzoku-meisai/uiItemsLogic'
import { shokushuTypeLabels, ShokushuTypeType, shokushuTypeType } from '@/repositories/keiei-joho/data'
import ShokushuSettingItem from '@/views/keiei-joho/keiei-info/ShokushuSettingItem.vue'
import { ResponseShokushuSettingItem } from '@/repositories/keiei-joho/shokushu/data'

const repository = keieiJohoRepositories.shokushuSetting

interface ShokushuTab {
  type: ShokushuTypeType;
  label: string;
  items: ShokushuUiItem[];
}

function settingCheck (
  allShokushuItemsMap: Ref<Record<ShokushuTypeType, ShokushuUiItem[]>>,
  selectedShokushuTypes: Ref<ShokushuTypeType[]>,
  allShokushuTypes: ShokushuTypeType[],
  shokushuTypeLabels: Record<ShokushuTypeType, string>,
  requestError: Ref<string | null>
): boolean {
  for (const sType of allShokushuTypes) {
    if (!selectedShokushuTypes.value.includes(sType)) {
      continue
    }

    const items = allShokushuItemsMap.value[sType]

    // 設定されている項目だけを抽出
    const enabledItems = items.filter(item => item.item.setting.targets.length > 0)

    // conflictGroupごとにconflictSideを集計
    const conflictMap = new Map<string, Set<string>>()

    for (const { item } of enabledItems) {
      const group = item.conflictGroup
      const side = item.conflictSide

      // 排他ルールが設定されていない場合はスキップ
      if (!group || !side) continue

      if (!conflictMap.has(group)) {
        conflictMap.set(group, new Set())
      }
      conflictMap.get(group)!.add(side)
    }

    // 排他ルール違反があるかチェック
    for (const [group, sides] of conflictMap.entries()) {
      if (sides.size > 1) {
        requestError.value = `職種「${shokushuTypeLabels[sType]}」では、「給料・賞与」と「給料賞与区分不可」は同時に設定できません。`
        return false
      }
    }
  }

  return true
}

export default defineComponent({
  components: {
    ShokushuSettingItem,
    ResponseResultAlert,
    ContentLayout,
    LoadingButton
  },
  setup () {
    const loading = ref(false)
    const isSucceedRequest = ref(false)
    const requestError = ref<string | null>(null)

    // 全職種の一覧（定義値から取得）
    const allShokushuTypes : ShokushuTypeType[] = Object.values(shokushuTypeType)

    // チェックされた職種
    const selectedShokushuTypes = ref<ShokushuTypeType[]>([])

    // 職種ごとの表示アイテムと初期化処理
    const allShokushuItemsMap = ref({} as Record<ShokushuTypeType, ShokushuUiItem[]>)
    const initializeShokushuItem = () => {
      for (const sType of allShokushuTypes) {
        allShokushuItemsMap.value[sType] = []
      }
    }

    // 全タブ（全職種に対応するタブ情報）
    const allShokushuTabs = computed<ShokushuTab[]>(() =>
      allShokushuTypes.map((sType) => ({
        type: sType,
        label: shokushuTypeLabels[sType],
        items: allShokushuItemsMap.value[sType] || []
      }))
    )

    // 表示対象のタブ（チェックされた職種）
    const visibleShokushuTabs = computed(() =>
      allShokushuTabs.value.filter(tab => selectedShokushuTypes.value.includes(tab.type))
    )

    onMounted(async () => {
      loading.value = true
      initializeShokushuItem()
      await callApiWrapper(requestError, async () => {
        await refreshUiData()
      })
      loading.value = false
    })

    // 登録
    const onClickRegister = async () => {
      onDismissAlert()
      if (!settingCheck(allShokushuItemsMap, selectedShokushuTypes, allShokushuTypes, shokushuTypeLabels, requestError)) return

      loading.value = true

      await callApiWrapper(requestError, async () => {
        let params: ResponseShokushuSettingItem[] = []
        for (const sType of allShokushuTypes) {
          if (!selectedShokushuTypes.value.includes(sType)) {
            continue
          }
          const items = allShokushuItemsMap.value[sType]
          params = params.concat(items.map((v: ShokushuUiItem) => v.item))
        }
        await repository.save(params)
        await refreshUiData()
        isSucceedRequest.value = true
      })
      loading.value = false
    }

    const onDismissAlert = () => {
      isSucceedRequest.value = false
      requestError.value = null
    }

    const onChangeInput = (items: ShokushuUiItem[], item: ShokushuUiItem) => {
      UiItemsLogic.updateUiItem(items, item)
    }

    const refreshUiData = async () => {
      const data = await repository.list()
      const itemsMap = {} as Record<ShokushuTypeType, ShokushuUiItem[]>
      const selectedTypesSet = new Set<ShokushuTypeType>()

      // 職種の中で1つでも設定科目があれば、その職種を選択対象とする
      for (const type of allShokushuTypes) {
        itemsMap[type] = data.items
          .filter(item => item.shokushuType === type && item.isRequiredSetting)
          .map(item => {
            if (item.setting.targets.length > 0) {
              selectedTypesSet.add(type)
            }
            return { item, isValid: true }
          })
      }

      allShokushuItemsMap.value = itemsMap
      selectedShokushuTypes.value = Array.from(selectedTypesSet)
    }

    return {
      selectedShokushuTypes,
      allShokushuTypes,

      loading,
      isSucceedRequest,
      requestError,
      shokushuTypeType,
      shokushuTypeLabels,
      visibleShokushuTabs,
      onClickRegister,
      onDismissAlert,
      onChangeInput
    }
  }
})
